const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';

// Track zoom-in listeners per-element so we can tear them down on section unload.
const ZOOM_IN_TEARDOWNS = new WeakMap();

// Scroll in animation logic
function onIntersection(elements, observer) {
  elements.forEach((element, index) => {
    if (element.isIntersecting) {
      const elementTarget = element.target;
      if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        if (elementTarget.hasAttribute('data-cascade'))
          elementTarget.setAttribute('style', `--animation-order: ${index};`);
      }
      observer.unobserve(elementTarget);
    } else {
      element.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
    });
    return;
  }

  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: '0px 0px -50px 0px',
  });
  animationTriggerElements.forEach((element) => observer.observe(element));
}

// Zoom in animation logic
//
// Previously this subscribed every zoom-in element to a throttled `window scroll`
// handler that recomputed `getBoundingClientRect()` for each element every 16 ms —
// N-elements × 60 fps layout reads on every scroll event, even when the elements
// were off-screen. That forced layout recalc on an otherwise-idle main thread and
// tanked scroll fps on pages with 5+ zoom-in sections.
//
// The replacement uses a single IntersectionObserver per element with a dense
// threshold list. The browser fires callbacks only when the element crosses a
// threshold (cheap — no layout reads on our side) and exposes the same
// intersection geometry (`entry.intersectionRatio`, `boundingClientRect`,
// `rootBounds`) we need to compute the original `percentageSeen()` value.
// Result: no scroll listeners, no throttle, same visual output.
function initializeScrollZoomAnimationTrigger() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const animationTriggerElements = Array.from(document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));

  if (animationTriggerElements.length === 0) return;

  const scaleAmount = 0.2 / 100;
  // Dense threshold list = smooth tween without a scroll handler.
  const thresholds = [];
  for (let t = 0; t <= 1; t += 0.05) thresholds.push(Math.min(1, Math.round(t * 100) / 100));

  animationTriggerElements.forEach((element) => {
    if (ZOOM_IN_TEARDOWNS.has(element)) return; // avoid stacking on re-init

    // Seed initial value from current scroll position — IntersectionObserver will
    // only fire on *change*, so if the element is already partially visible the
    // variable needs to be set once up front.
    element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeenFromRect(element.getBoundingClientRect(), element.offsetHeight));

    const observer = new IntersectionObserver((entries) => {
      // Each entry carries both bounds and viewport rects, so we can compute the
      // original `percentageSeen` purely from what the callback gives us — no
      // additional getBoundingClientRect call here. This keeps the observer
      // callback layout-thrash-free.
      for (let i = 0; i < entries.length; i++) {
        const entry = entries[i];
        const pct = percentageSeenFromRect(entry.boundingClientRect, entry.target.offsetHeight, entry.rootBounds);
        entry.target.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * pct);
      }
    }, { threshold: thresholds });

    observer.observe(element);

    ZOOM_IN_TEARDOWNS.set(element, () => {
      observer.unobserve(element);
      observer.disconnect();
    });
  });
}

// Pure function: same math as percentageSeen() below, but parameterized on the
// rects supplied by IntersectionObserver entries so we never re-read layout.
function percentageSeenFromRect(rect, elementHeight, rootBounds) {
  const viewportHeight = (rootBounds && rootBounds.height) || window.innerHeight;
  // rect.top is viewport-relative (same as getBoundingClientRect().top), so we can
  // mirror the pre-refactor math without needing scrollY.
  const elementTopInViewport = rect.top;
  if (elementTopInViewport > viewportHeight) return 0; // not yet reached
  if (elementTopInViewport + elementHeight < 0) return 100; // scrolled past
  const distance = viewportHeight - elementTopInViewport;
  const percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

function teardownScrollZoomAnimationTrigger(rootEl = document) {
  const elements = rootEl.getElementsByClassName
    ? Array.from(rootEl.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME))
    : [];
  elements.forEach((element) => {
    const teardown = ZOOM_IN_TEARDOWNS.get(element);
    if (teardown) {
      teardown();
      ZOOM_IN_TEARDOWNS.delete(element);
    }
  });
}

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) {
    // If we haven't reached the image yet
    return 0;
  } else if (elementPositionY + elementHeight < scrollY) {
    // If we've completely scrolled past the image
    return 100;
  }

  // When the image is in the viewport
  const distance = scrollY + viewportHeight - elementPositionY;
  let percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

window.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
});

// bfcache restore does not re-run DOMContentLoaded; IntersectionObserver may not
// re-fire, leaving sections stuck with .scroll-trigger--offscreen (~invisible).
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  document.querySelectorAll('#MainContent .scroll-trigger--offscreen').forEach((el) => {
    el.classList.remove('scroll-trigger--offscreen');
  });
});

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => initializeScrollAnimationTrigger(event.target, true));
  document.addEventListener('shopify:section:reorder', () => initializeScrollAnimationTrigger(document, true));
}

// Tear down per-element IntersectionObservers + scroll listeners when a section
// is removed or re-rendered by the theme editor to prevent leaks.
document.addEventListener('shopify:section:unload', (event) => {
  teardownScrollZoomAnimationTrigger(event.target);
});
